import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Heart, User, UserCircle2, Baby, Users, Sparkles } from "lucide-react";

/* Stimmen-Familie · Konstellation
 * 6 voices (Vater · Mutter · Männer · Frauen · Kinder · Eltern) as pulsating hearts
 * arranged in an orbital lens, with HERZBEWEGUNG as the overarching arch.
 * Hover: preview panel with slide count & duration. Click: navigate to deck.
 */

const voices = [
  {
    key: "vaterstimme",
    label: "Vater",
    subtitle: "Würde · Verantwortung",
    color: "#D4AF37",
    glow: "rgba(212,175,55,0.35)",
    slides: 12,
    minutes: 6,
    Icon: Shield,
    route: "/vaterstimme/deck",
  },
  {
    key: "mutterstimme",
    label: "Mutter",
    subtitle: "Selbstfürsorge · Balance",
    color: "#C77B7B",
    glow: "rgba(199,123,123,0.4)",
    slides: 15,
    minutes: 8,
    Icon: Heart,
    route: "/mutterstimme/deck",
  },
  {
    key: "maennerstimme",
    label: "Männer",
    subtitle: "Klarheit · Kraft",
    color: "#8E7CC3",
    glow: "rgba(142,124,195,0.4)",
    slides: 15,
    minutes: 8,
    Icon: User,
    route: "/maennerstimme/deck",
  },
  {
    key: "frauenstimme",
    label: "Frauen",
    subtitle: "Weisheit · Vermächtnis",
    color: "#E8B4A2",
    glow: "rgba(232,180,162,0.4)",
    slides: 15,
    minutes: 8,
    Icon: UserCircle2,
    route: "/frauenstimme/deck",
  },
  {
    key: "kinderstimme",
    label: "Kinder",
    subtitle: "Licht · Potenzial",
    color: "#1A3B6E",
    glow: "rgba(26,59,110,0.4)",
    slides: 10,
    minutes: 5,
    Icon: Baby,
    route: "/kinderstimme/deck",
  },
  {
    key: "elternstimme",
    label: "Eltern",
    subtitle: "Familie · Zukunft",
    color: "#B08D2F",
    glow: "rgba(212,175,55,0.4)",
    slides: 16,
    minutes: 9,
    Icon: Users,
    route: "/elternstimme/deck",
  },
];

const HERZ_GOLD = "#D4AF37";
const HERZ_TEAL = "#0F4F4F";

export default function StimmenFamilieConstellation() {
  const [hovered, setHovered] = useState(null);

  return (
    <section
      className="relative mb-16 rounded-3xl overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, rgba(15,79,79,0.08) 0%, rgba(212,175,55,0.05) 50%, rgba(15,79,79,0.08) 100%)",
        border: `1px solid ${HERZ_GOLD}25`,
      }}
      data-testid="stimmen-familie-constellation"
    >
      {/* Ambient glow */}
      <div
        className="absolute -top-1/2 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full blur-3xl opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(212,175,55,0.15) 0%, transparent 60%)" }}
      />

      <div className="relative px-6 md:px-10 py-10 md:py-14">
        {/* Header */}
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center gap-3 text-[10px] tracking-[0.4em] uppercase font-semibold mb-3"
            style={{ color: HERZ_GOLD }}
          >
            <Sparkles size={12} /> Ein zusammengehöriges Werk <Sparkles size={12} />
          </div>
          <h2
            className="serif font-light leading-tight tracking-tight"
            style={{ fontSize: "clamp(2rem,3.8vw,3.4rem)", color: "#F5EBE0" }}
          >
            Die <span style={{ color: HERZ_GOLD }}>Stimmen-Familie</span>.
          </h2>
          <p
            className="serif italic mt-3 max-w-2xl mx-auto"
            style={{ color: "rgba(245,235,224,0.7)", fontSize: "clamp(0.95rem,1.3vw,1.15rem)" }}
          >
            Sechs Stimmen. Ein Herz. Ein Ökosystem der Menschlichkeit.
          </p>
        </div>

        {/* Herzbewegung Arch — overarching bond */}
        <Link
          to="/herzbewegung/deck"
          className="block group relative mx-auto mb-6 max-w-3xl"
          onMouseEnter={() => setHovered("herzbewegung")}
          onMouseLeave={() => setHovered(null)}
          data-testid="stimmen-familie-herzbewegung-arch"
        >
          <svg viewBox="0 0 600 90" className="w-full h-auto">
            <defs>
              <linearGradient id="archGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={HERZ_TEAL} stopOpacity="0.2" />
                <stop offset="50%" stopColor={HERZ_GOLD} stopOpacity="0.8" />
                <stop offset="100%" stopColor={HERZ_TEAL} stopOpacity="0.2" />
              </linearGradient>
            </defs>
            <path
              d="M 30 85 Q 300 -30 570 85"
              fill="none"
              stroke="url(#archGrad)"
              strokeWidth="2"
              strokeDasharray="3 5"
              className="transition-all duration-500 group-hover:stroke-[3]"
            />
            {/* Infinity Heart at top */}
            <g transform="translate(275 5)">
              <path
                d="M12 8 C 6 5 2 12 8 18 C 14 24 25 18 25 18 C 25 18 36 24 42 18 C 48 12 44 5 38 8 C 33 10 27 16 25 22 C 23 16 17 10 12 8 Z"
                fill={HERZ_GOLD}
                className="transition-all duration-500"
                style={{ filter: hovered === "herzbewegung" ? "drop-shadow(0 0 12px rgba(212,175,55,0.9))" : "drop-shadow(0 0 4px rgba(212,175,55,0.4))" }}
              />
            </g>
          </svg>
          <div
            className="absolute left-1/2 -translate-x-1/2 -bottom-2 text-center transition-all duration-500"
            style={{ transform: `translateX(-50%) translateY(${hovered === "herzbewegung" ? "6px" : "0"})` }}
          >
            <div className="text-[9px] tracking-[0.4em] uppercase font-bold" style={{ color: HERZ_GOLD }}>
              HERZBEWEGUNG™
            </div>
            <div
              className="text-[10px] mt-0.5 transition-opacity duration-500"
              style={{ color: "rgba(245,235,224,0.6)", opacity: hovered === "herzbewegung" ? 1 : 0.5 }}
            >
              Der übergeordnete Bogen · 9 Folien
            </div>
          </div>
        </Link>

        {/* Six voices — orbital arrangement */}
        <div className="relative mt-14 grid grid-cols-3 md:grid-cols-6 gap-4 md:gap-2">
          {/* Connection SVG (behind hearts) */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none hidden md:block"
            viewBox="0 0 600 200"
            preserveAspectRatio="none"
          >
            <path
              d="M 50 100 L 550 100"
              fill="none"
              stroke={HERZ_GOLD}
              strokeWidth="1"
              strokeDasharray="2 4"
              opacity="0.3"
            />
          </svg>

          {voices.map((v, i) => {
            const isHovered = hovered === v.key;
            const isDimmed = hovered && hovered !== v.key && hovered !== "herzbewegung";
            const Icon = v.Icon;
            return (
              <Link
                key={v.key}
                to={v.route}
                onMouseEnter={() => setHovered(v.key)}
                onMouseLeave={() => setHovered(null)}
                className="group relative flex flex-col items-center transition-all duration-500"
                style={{
                  opacity: isDimmed ? 0.35 : 1,
                  transform: isHovered ? "translateY(-6px) scale(1.05)" : "translateY(0)",
                }}
                data-testid={`stimmen-familie-voice-${v.key}`}
              >
                {/* Pulse glow */}
                <div
                  className="absolute top-0 w-28 h-28 rounded-full blur-2xl pointer-events-none transition-opacity duration-500"
                  style={{
                    background: `radial-gradient(circle, ${v.glow} 0%, transparent 70%)`,
                    opacity: isHovered ? 1 : 0.5,
                  }}
                />

                {/* Heart body */}
                <div
                  className="relative w-20 h-20 md:w-24 md:h-24 flex items-center justify-center transition-all duration-700"
                  style={{
                    animation: `stimmenPulse ${3 + i * 0.3}s ease-in-out infinite`,
                    animationDelay: `${i * 0.2}s`,
                  }}
                >
                  <svg viewBox="0 0 60 54" className="w-full h-full">
                    <defs>
                      <radialGradient id={`hg-${v.key}`} cx="50%" cy="35%" r="55%">
                        <stop offset="0%" stopColor={v.color} stopOpacity="1" />
                        <stop offset="100%" stopColor={v.color} stopOpacity="0.75" />
                      </radialGradient>
                    </defs>
                    <path
                      d="M30 50 C -5 30 5 5 20 8 C 25 9 28 13 30 18 C 32 13 35 9 40 8 C 55 5 65 30 30 50 Z"
                      fill={`url(#hg-${v.key})`}
                      stroke={v.color}
                      strokeWidth="1.5"
                      style={{
                        filter: `drop-shadow(0 4px 12px ${v.glow})`,
                      }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center pt-0.5">
                    <Icon size={22} strokeWidth={1.8} style={{ color: "rgba(255,255,255,0.95)" }} />
                  </div>
                </div>

                {/* Label */}
                <div
                  className="mt-3 text-center transition-all duration-500"
                  style={{ color: isHovered ? v.color : "rgba(245,235,224,0.85)" }}
                >
                  <div className="text-[11px] md:text-xs tracking-[0.28em] uppercase font-bold">
                    {v.label}
                  </div>
                  <div
                    className="text-[9px] md:text-[10px] mt-0.5 opacity-70"
                    style={{ color: "rgba(245,235,224,0.55)" }}
                  >
                    {v.subtitle}
                  </div>
                </div>

                {/* Hover preview badge */}
                <div
                  className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap pointer-events-none transition-all duration-300"
                  style={{ opacity: isHovered ? 1 : 0, transform: `translateX(-50%) translateY(${isHovered ? 0 : -4}px)` }}
                >
                  <div
                    className="px-3 py-1 rounded-full text-[9px] tracking-[0.15em] uppercase font-semibold flex items-center gap-1.5"
                    style={{
                      background: v.color,
                      color: "#FFFFFF",
                      boxShadow: `0 6px 20px ${v.glow}`,
                    }}
                  >
                    {v.slides} Folien · ~{v.minutes} Min
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Bottom footer note */}
        <div className="mt-16 text-center">
          <div
            className="inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase"
            style={{ color: "rgba(245,235,224,0.5)" }}
          >
            <span style={{ color: HERZ_GOLD }}>◆</span>
            Klicke ein Herz — betritt eine Stimme
            <span style={{ color: HERZ_GOLD }}>◆</span>
          </div>
        </div>
      </div>

      {/* Pulse keyframes */}
      <style>{`
        @keyframes stimmenPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.045); }
        }
      `}</style>
    </section>
  );
}
